"use client";

import { makeVar } from "@apollo/client";
import { useReactiveVar } from "@apollo/client/react";
import type { ReactElement, ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/utils/lib";

type ConfirmState = {
  title?: string;
  description?: string | ReactNode;
  primaryCTA?: string;
  cancelCTA?: string;
  type?: "warning" | "success";
};

const confirmState = makeVar<ConfirmState | null>(null);
let activeConfirmResolve: ((value: boolean) => void) | null = null;

const resolveConfirm = (result: boolean) => {
  const resolve = activeConfirmResolve;
  activeConfirmResolve = null;
  confirmState(null);
  resolve?.(result);
};

export const useConfirm = () => {
  const confirm = ({
    title = "Please confirm",
    description = "You sure you want to continue with this action?",
    primaryCTA = "Confirm",
    cancelCTA = "Cancel",
    type,
  }: Partial<ConfirmState>) => {
    activeConfirmResolve?.(false);

    return new Promise<boolean>((resolve) => {
      activeConfirmResolve = resolve;
      confirmState({
        title,
        description,
        primaryCTA,
        cancelCTA,
        type,
      });
    });
  };

  return {
    confirm,
  };
};

export const ConfirmProvider = () => {
  const state = useReactiveVar(confirmState);

  if (!state) return null;

  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{state.title}</AlertDialogTitle>
          {typeof state.description === "string" || !state.description ? (
            <AlertDialogDescription>{state.description}</AlertDialogDescription>
          ) : (
            <AlertDialogDescription
              render={state.description as ReactElement}
            />
          )}
        </AlertDialogHeader>
        <AlertDialogFooter
          className="
            w-full flex-row items-center justify-center
            sm:justify-start
          "
        >
          <AlertDialogCancel
            onClick={() => resolveConfirm(false)}
            className="mt-0"
          >
            {state.cancelCTA}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => resolveConfirm(true)}
            className={cn(
              {
                "bg-green-500 text-green-900 hover:border-green-500 hover:bg-green-600 hover:text-white":
                  state.type === "success",
                "bg-red-500 text-red-100 hover:border-red-500 hover:bg-red-700 hover:text-white":
                  state.type === "warning",
              },
              "mt-0",
            )}
          >
            {state.primaryCTA}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
